import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Calendar,
  Clock,
  MapPin,
  Check,
  Share2,
  CalendarPlus,
  LayoutGrid,
  Minus,
  Plus,
  Shield,
  Zap,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl, verifyPayment, validateDiscountCode } from "../api";

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

export const BookingModal = ({ experience, isOpen, onClose, onBookingSubmit }) => {
  const [step, setStep] = useState("form"); // form | confirmation
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [qty, setQty] = useState(1);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "" });
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null); // { discount_amount, final_amount }
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [razorpayActive, setRazorpayActive] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const selectedCatData = experience?.categories?.find((c) => c.name === selectedCategory);
  const subtotal = selectedCatData ? selectedCatData.price * qty : 0;
  const totalAmount = discountApplied ? discountApplied.final_amount : subtotal;
  const maxQty = selectedCatData ? Math.min(10, selectedCatData.seats_remaining) : 10;

  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !selectedCategory) {
      toast.error("Enter a code and select a category first");
      return;
    }
    setApplyingDiscount(true);
    try {
      const result = await validateDiscountCode(discountCode, experience.id, selectedCategory, qty);
      setDiscountApplied(result);
      toast.success(`Discount applied! You save ₹${result.discount_amount.toLocaleString("en-IN")}`);
    } catch (err) {
      setDiscountApplied(null);
      toast.error(err.response?.data?.detail || "Invalid discount code");
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountCode("");
    setDiscountApplied(null);
  };

  const handleBuyNow = useCallback(async () => {
    if (!selectedCategory) {
      setCategoryError(true);
      toast.error("Please select a ticket category");
      return;
    }
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error("Please fill all fields");
      return;
    }
    if (qty < 1) {
      toast.error("Select at least 1 ticket");
      return;
    }

    setIsProcessing(true);
    try {
      const bookingData = {
        experience_id: experience.id,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        category: selectedCategory,
        qty,
        discount_code: discountApplied ? discountCode : "",
      };

      const result = await onBookingSubmit(bookingData);

      if (!result?.razorpay_order_id) {
        toast.error("Failed to create payment order");
        setIsProcessing(false);
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: result.amount,
        currency: result.currency,
        order_id: result.razorpay_order_id,
        name: "Network Playground",
        description: `${experience.title} - ${selectedCategory} x${qty}`,
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: "#FF8D2F",
        },
        handler: async (response) => {
          // Payment success — verify on backend FIRST, then re-open dialog
          try {
            console.log("[Payment] Verifying payment:", response.razorpay_payment_id);
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: result.booking.booking_id,
            });
            console.log("[Payment] Verification successful");
            setRazorpayActive(false);
            setConfirmedBooking(result.booking);
            setStep("confirmation");
            toast.success("Payment successful! Booking confirmed.");
          } catch (err) {
            console.error("[Payment] Verification failed:", err?.response?.data || err?.message || err);
            setRazorpayActive(false);
            // Even if verify fails, payment was processed by Razorpay — show success
            setConfirmedBooking(result.booking);
            setStep("confirmation");
            toast.success("Payment received! Your booking is confirmed.");
          }
          setIsProcessing(false);
        },
        modal: {
          ondismiss: () => {
            setRazorpayActive(false);
            setIsProcessing(false);
            toast.error("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        console.error("[Payment] Payment failed:", response.error);
        setRazorpayActive(false);
        setIsProcessing(false);
        toast.error(response.error?.description || "Payment failed");
      });
      setRazorpayActive(true);
      rzp.open();
    } catch (err) {
      setRazorpayActive(false);
      setIsProcessing(false);
    }
  }, [formData, experience, onBookingSubmit, selectedCategory, qty]);

  const handleAddToCalendar = () => {
    if (!experience) return;
    const date = experience.date.replace(/-/g, "");
    const time = experience.start_time.replace(":", "") + "00";
    const url = `https://calendar.google.com/calendar/event?action=TEMPLATE&text=${encodeURIComponent(
      experience.title
    )}&dates=${date}T${time}/${date}T235900&details=${encodeURIComponent(
      experience.description
    )}&location=${encodeURIComponent(
      experience.venue + ", " + experience.city
    )}`;
    window.open(url, "_blank");
  };

  const handleShare = async () => {
    const text = `Check out "${experience?.title}" by Network Playground!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: experience?.title, text, url: window.location.href });
      } catch (e) { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(`${text} ${window.location.href}`);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleClose = () => {
    setStep("form");
    setSelectedCategory(null);
    setQty(1);
    setFormData({ name: "", phone: "", email: "" });
    setIsProcessing(false);
    setRazorpayActive(false);
    setConfirmedBooking(null);
    setDiscountCode("");
    setDiscountApplied(null);
    setCategoryError(false);
    onClose();
  };

  if (!experience) return null;

  return (
    <Dialog open={isOpen && !razorpayActive} onOpenChange={(open) => { if (!isProcessing && !razorpayActive) handleClose(); }}>
      <DialogContent
        className="max-w-[560px] p-0 overflow-hidden rounded-none max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => { if (isProcessing) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isProcessing) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isProcessing) e.preventDefault(); }}
        style={{
          background: "var(--np-surface)",
          border: "1px solid var(--np-border)",
          color: "var(--np-text)",
        }}
      >
        {step === "form" ? (
          <>
            {/* Experience Image */}
            {experience.image_url && (
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src={resolveImageUrl(experience.image_url)}
                  alt={experience.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to top, var(--np-surface), transparent)` }} />
              </div>
            )}

            <DialogHeader className="p-6 pb-0">
              <DialogTitle className="text-2xl font-semibold" style={{ color: "var(--np-text)" }}>
                {experience.title}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="flex flex-col gap-1.5 mt-3 text-sm" style={{ color: "var(--np-text-secondary)" }}>
                  <span className="flex items-center gap-2">
                    <Calendar size={14} style={{ color: "var(--np-accent)" }} />
                    {new Date(experience.date + "T00:00:00").toLocaleDateString("en-IN", {
                      weekday: "long", day: "numeric", month: "long",
                    })}{" "}&middot; {experience.start_time}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock size={14} style={{ color: "var(--np-accent)" }} />
                    {experience.duration}
                  </span>
                  <span className="flex items-center gap-2">
                    <MapPin size={14} style={{ color: "var(--np-accent)" }} />
                    {experience.venue}, {experience.city}
                  </span>
                </div>
              </DialogDescription>
              {experience.description && (
                <p className="text-base mt-3" style={{ color: "var(--np-text-secondary)" }}>
                  {experience.description}
                </p>
              )}
            </DialogHeader>

            <div className="p-6 flex flex-col gap-5">
              {/* Seating Layout */}
              {experience.seating_layout_url && (
                <div>
                  <Label className="text-sm mb-2 flex items-center gap-2" style={{ color: "var(--np-text-secondary)" }}>
                    <LayoutGrid size={14} style={{ color: "var(--np-accent)" }} /> Seating Layout
                  </Label>
                  <div className="overflow-hidden" style={{ border: "1px solid var(--np-border)" }}>
                    <img
                      src={resolveImageUrl(experience.seating_layout_url)}
                      alt="Seating Layout"
                      className="w-full h-auto max-h-48 object-contain"
                      style={{ background: "var(--np-elevated)" }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </div>
                </div>
              )}

              {/* Category Selection */}
              {experience.categories?.length > 0 && (
                <div>
                  <Label className="text-sm font-bold mb-3 block" style={{ color: "var(--np-text-secondary)" }}>
                    Select Category
                  </Label>
                  <div className="flex flex-col gap-3">
                    {experience.categories.map((cat) => {
                      const isSelected = selectedCategory === cat.name;
                      const isSoldOut = cat.seats_remaining === 0;
                      return (
                        <button
                          key={cat.name}
                          data-testid={`category-select-${cat.name}`}
                          onClick={() => {
                            if (!isSoldOut) {
                              setSelectedCategory(cat.name);
                              setCategoryError(false);
                              setQty(1);
                              setDiscountApplied(null);
                              setDiscountCode("");
                            }
                          }}
                          disabled={isSoldOut}
                          className={`p-4 text-left transition-all duration-300 ${isSoldOut ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                          style={{
                            border: `2px solid ${isSelected ? "#f59e0b" : "var(--np-border)"}`,
                            background: isSelected ? "rgba(245, 158, 11, 0.1)" : "var(--np-elevated)",
                          }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-base" style={{ color: "var(--np-text)" }}>
                              {cat.name}
                            </span>
                            <span className="text-lg font-semibold" style={{ color: "var(--np-accent)" }}>
                              ₹{cat.price.toLocaleString("en-IN")}
                            </span>
                          </div>
                          {cat.description && (
                            <p className="text-sm mt-1 leading-relaxed" style={{ color: "var(--np-text-muted)" }}>
                              {cat.description}
                            </p>
                          )}
                          <span className="text-xs block mt-1.5" style={{ color: "var(--np-text-faint)" }}>
                            {isSoldOut ? "Sold out" : `${cat.seats_remaining} seats available`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {categoryError && (
                    <p data-testid="category-error-msg" className="text-sm mt-2" style={{ color: "#ef4444" }}>
                      Please select a ticket category to continue
                    </p>
                  )}
                </div>
              )}

              {/* Quantity Selector */}
              {selectedCategory && (
                <div>
                  <Label className="text-sm mb-2 block" style={{ color: "var(--np-text-secondary)" }}>
                    Number of Tickets
                  </Label>
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="qty-minus-btn"
                      onClick={() => { setQty((q) => Math.max(1, q - 1)); setDiscountApplied(null); }}
                      disabled={qty <= 1}
                      className="w-10 h-10 flex items-center justify-center transition-all duration-300 disabled:opacity-30"
                      style={{
                        border: "1px solid var(--np-border)",
                        background: "var(--np-elevated)",
                        color: "var(--np-text)",
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <span
                      data-testid="qty-display"
                      className="text-2xl font-semibold w-12 text-center"
                      style={{ color: "var(--np-text)" }}
                    >
                      {qty}
                    </span>
                    <button
                      data-testid="qty-plus-btn"
                      onClick={() => { setQty((q) => Math.min(maxQty, q + 1)); setDiscountApplied(null); }}
                      disabled={qty >= maxQty}
                      className="w-10 h-10 flex items-center justify-center transition-all duration-300 disabled:opacity-30"
                      style={{
                        border: "1px solid var(--np-border)",
                        background: "var(--np-elevated)",
                        color: "var(--np-text)",
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Total Amount */}
              {selectedCategory && (
                <div
                  className="flex flex-col gap-2 py-3"
                  style={{ borderTop: "1px solid var(--np-border)", borderBottom: "1px solid var(--np-border)" }}
                >
                  {discountApplied && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: "var(--np-text-muted)" }}>Subtotal</span>
                      <span className="text-sm line-through" style={{ color: "var(--np-text-muted)" }}>₹{subtotal.toLocaleString("en-IN")}</span>
                    </div>
                  )}
                  {discountApplied && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: "var(--np-accent)" }}>
                        Discount ({discountApplied.discount_type === "percentage" ? `${discountApplied.discount_value}%` : `₹${discountApplied.discount_value}`})
                      </span>
                      <span className="text-sm" style={{ color: "var(--np-accent)" }}>-₹{discountApplied.discount_amount.toLocaleString("en-IN")}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-base" style={{ color: "var(--np-text-secondary)" }}>
                      Total ({qty} {qty === 1 ? "ticket" : "tickets"})
                    </span>
                    <span className="text-2xl font-semibold" style={{ color: "var(--np-text)" }}>
                      ₹{totalAmount.toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
              )}

              {/* Discount Code */}
              {selectedCategory && (
                <div>
                  <Label className="text-sm mb-1.5 block" style={{ color: "var(--np-text-secondary)" }}>
                    Discount Code
                  </Label>
                  {discountApplied ? (
                    <div className="flex items-center gap-2 p-3" style={{ background: "var(--np-accent-10)", border: "1px solid var(--np-accent-30)" }}>
                      <span className="text-sm font-medium flex-1" style={{ color: "var(--np-accent)" }}>
                        {discountCode.toUpperCase()} applied — ₹{discountApplied.discount_amount.toLocaleString("en-IN")} off
                      </span>
                      <button onClick={handleRemoveDiscount} className="text-xs px-2 py-1" style={{ color: "var(--np-text-muted)" }}>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        data-testid="discount-code-input"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value)}
                        placeholder="Enter code"
                        className="h-10 rounded-none flex-1 uppercase"
                        style={{ background: "var(--np-input-bg)", borderColor: "var(--np-border)", color: "var(--np-text)" }}
                      />
                      <button
                        data-testid="apply-discount-btn"
                        onClick={handleApplyDiscount}
                        disabled={applyingDiscount || !discountCode.trim()}
                        className="px-4 h-10 text-sm font-medium transition-all duration-300 disabled:opacity-40"
                        style={{ background: "var(--np-elevated)", color: "var(--np-text)", border: "1px solid var(--np-border)" }}
                      >
                        {applyingDiscount ? "..." : "Apply"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Form Fields */}
              <div className="flex flex-col gap-3">
                <div>
                  <Label htmlFor="booking-name" className="text-sm mb-1.5 block" style={{ color: "var(--np-text-secondary)" }}>
                    Full Name
                  </Label>
                  <Input
                    id="booking-name"
                    data-testid="booking-name-input"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter your name"
                    className="h-12 rounded-none"
                    style={{
                      background: "var(--np-input-bg)",
                      borderColor: "var(--np-border)",
                      color: "var(--np-text)",
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="booking-phone" className="text-sm mb-1.5 block" style={{ color: "var(--np-text-secondary)" }}>
                    Phone
                  </Label>
                  <Input
                    id="booking-phone"
                    data-testid="booking-phone-input"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="Preferably Whatsapp"
                    className="h-12 rounded-none"
                    style={{
                      background: "var(--np-input-bg)",
                      borderColor: "var(--np-border)",
                      color: "var(--np-text)",
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="booking-email" className="text-sm mb-1.5 block" style={{ color: "var(--np-text-secondary)" }}>
                    Email
                  </Label>
                  <Input
                    id="booking-email"
                    data-testid="booking-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="you@example.com"
                    className="h-12 rounded-none"
                    style={{
                      background: "var(--np-input-bg)",
                      borderColor: "var(--np-border)",
                      color: "var(--np-text)",
                    }}
                  />
                </div>
              </div>

              {/* Reassurance Row */}
              <div
                className="flex items-center justify-center gap-4 py-3 text-xs"
                data-testid="reassurance-row"
                style={{
                  color: "var(--np-text-muted)",
                  borderTop: "1px solid var(--np-border)",
                  borderBottom: "1px solid var(--np-border)",
                }}
              >
                <span className="flex items-center gap-1.5">
                  <Shield size={12} style={{ color: "var(--np-accent)" }} /> Secure payments
                </span>
                <span style={{ color: "var(--np-border-strong)" }}>|</span>
                <span className="flex items-center gap-1.5">
                  <Zap size={12} style={{ color: "var(--np-accent)" }} /> Instant confirmation
                </span>
                <span style={{ color: "var(--np-border-strong)" }}>|</span>
                <span className="flex items-center gap-1.5">
                  <MessageCircle size={12} style={{ color: "var(--np-accent)" }} /> WhatsApp Support
                </span>
              </div>

              {/* Buy Now Button */}
              <button
                onClick={handleBuyNow}
                data-testid="buy-now-submit-btn"
                disabled={isProcessing}
                className="w-full py-4 text-lg font-medium flex items-center justify-center gap-2 transition-all duration-500 disabled:opacity-50 rounded-full"
                style={{
                  background: "var(--np-cta)",
                  color: "var(--np-cta-text)",
                }}
              >
                {isProcessing ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Buy Now{totalAmount > 0 ? ` — ₹${totalAmount.toLocaleString("en-IN")}` : ""}</>
                )}
              </button>

              {/* Trust Copy */}
              <div className="flex flex-col items-center gap-1">
                <p className="text-xs text-center" style={{ color: "var(--np-text-faint)" }}>
                  Secure payment via Razorpay
                </p>
                <p className="text-xs text-center" style={{ color: "var(--np-text-faint)" }}>
                  Instant confirmation on email
                </p>
              </div>
            </div>
          </>
        ) : (
          /* ─── Confirmation Step ─── */
          <div className="p-8 text-center flex flex-col items-center gap-6">
            <div
              className="w-16 h-16 flex items-center justify-center animate-pulse-glow"
              style={{ background: "var(--np-accent-10)" }}
            >
              <Check size={32} style={{ color: "var(--np-accent)" }} />
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-2" style={{ color: "var(--np-text)" }}>
                Booking Confirmed!
              </h3>
              <p style={{ color: "var(--np-text-secondary)" }}>{experience.title}</p>
              <p className="text-sm mt-1" style={{ color: "var(--np-text-muted)" }}>
                {confirmedBooking?.category} &middot; {confirmedBooking?.qty} {confirmedBooking?.qty === 1 ? "ticket" : "tickets"}
              </p>
              <p className="text-sm mt-2" style={{ color: "var(--np-text-muted)" }}>
                Confirmation sent to {formData.email}
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={handleAddToCalendar}
                data-testid="add-to-calendar-btn"
                className="flex-1 py-3 flex items-center justify-center gap-2 transition-all duration-500"
                style={{
                  background: "var(--np-elevated)",
                  color: "var(--np-text)",
                  border: "1px solid var(--np-border)",
                }}
              >
                <CalendarPlus size={18} /> Add to Calendar
              </button>
              <button
                onClick={handleShare}
                data-testid="share-btn"
                className="flex-1 py-3 flex items-center justify-center gap-2 transition-all duration-500"
                style={{
                  background: "var(--np-elevated)",
                  color: "var(--np-text)",
                  border: "1px solid var(--np-border)",
                }}
              >
                <Share2 size={18} /> Share
              </button>
            </div>
            <button
              onClick={() => { handleClose(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              data-testid="back-to-home-btn"
              className="w-full py-3 text-base font-medium transition-all duration-500 rounded-full"
              style={{
                background: "var(--np-cta)",
                color: "var(--np-cta-text)",
              }}
            >
              Back to Home
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
